import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FiPlus, FiChevronDown, FiSearch } from 'react-icons/fi';
import { useDatabase } from '../../hooks/useDatabase';
import { useCurrentPerson } from '../../hooks/useCurrentPerson';
import PersonMappingPrompt from '../../components/common/PersonMappingPrompt';
import NativeStakeholderForm from '../../components/forms/NativeStakeholderForm';
import StakeholderDashboard from '../../components/dashboards/StakeholderDashboard';

const YourInterestsPage: React.FC = () => {
  const { database, applyCommand } = useDatabase();
  const { personId, setPersonId, stakeholderIds, isMapped, loading: personLoading } = useCurrentPerson();
  const [activeStakeholderId, setActiveStakeholderId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showJoinPicker, setShowJoinPicker] = useState(false);
  const [joinQuery, setJoinQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Stakeholders the user is NOT already a member of
  const joinableStakeholders = useMemo(() => {
    if (!database) return [];
    const currentSet = new Set(stakeholderIds);
    const all = database.stakeholders();
    return Object.entries(all)
      .filter(([id]) => !currentSet.has(id))
      .map(([id, s]) => ({ id, name: s.name() }));
  }, [database, stakeholderIds]);

  const filteredJoinable = useMemo(() => {
    if (!joinQuery.trim()) return joinableStakeholders;
    const q = joinQuery.toLowerCase();
    return joinableStakeholders.filter(s => s.name.toLowerCase().includes(q));
  }, [joinableStakeholders, joinQuery]);

  const joinStakeholder = async (stakeholderId: string) => {
    if (!personId) return;
    await applyCommand({
      type: 'SetStakeholderRepresentative',
      representative: { stakeholder_id: stakeholderId, person_id: personId },
      enabled: true,
    });
    setShowJoinPicker(false);
    setJoinQuery('');
    setActiveStakeholderId(stakeholderId);
  };

  const stakeholders = useMemo(() => {
    if (!database || stakeholderIds.length === 0) return [];
    const map = database.stakeholders();
    return stakeholderIds
      .map((id) => {
        const s = map[id];
        return s ? { id, name: s.name() } : null;
      })
      .filter(Boolean) as { id: string; name: string }[];
  }, [database, stakeholderIds]);

  const selectedId = activeStakeholderId || (stakeholders.length > 0 ? stakeholders[0].id : null);

  useEffect(() => {
    if (!showDropdown) return;
    const handle = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showDropdown]);

  if (personLoading) {
    return (
      <div className="flex items-center gap-3 justify-center p-12 text-cm-text-muted text-sm">
        <div className="w-5 h-5 border-2 border-cm-accent-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isMapped) {
    return <PersonMappingPrompt onSelect={setPersonId} />;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-semibold text-cm-text-primary">Your Interests</h1>
          <p className="mt-1 text-sm text-cm-text-secondary">
            Priorities you're tracking as a stakeholder — deliveries, budgets, and confidence levels.
          </p>
        </div>

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cm-accent-purple text-cm-bg-deep font-mono text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <FiPlus className="w-4 h-4" />
            Add group
            <FiChevronDown className="w-3.5 h-3.5" />
          </button>
          {showDropdown && (
            <div className="absolute right-0 mt-1 w-52 bg-cm-bg-elevated border border-cm-border-mid rounded-lg shadow-xl z-50 py-1">
              <button
                className="w-full px-4 py-2 text-sm text-cm-text-secondary hover:bg-cm-bg-hover hover:text-cm-text-primary text-left transition-colors"
                onClick={() => { setShowDropdown(false); setShowJoinPicker(true); setJoinQuery(''); }}
              >
                Join an existing group
              </button>
              <button
                className="w-full px-4 py-2 text-sm text-cm-text-secondary hover:bg-cm-bg-hover hover:text-cm-text-primary text-left transition-colors"
                onClick={() => { setShowDropdown(false); setShowCreateForm(true); }}
              >
                Create a new group
              </button>
            </div>
          )}
        </div>
      </div>

      {stakeholders.length === 0 ? (
        <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-8 text-center">
          <p className="text-cm-text-muted mb-4">You are not a representative of any stakeholder groups.</p>
          <p className="text-sm text-cm-text-muted">
            Use the <span className="font-medium text-cm-accent-purple">Add group</span> button above to create or join a stakeholder group.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 border-b border-cm-border-subtle overflow-x-auto">
            {stakeholders.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveStakeholderId(s.id)}
                className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${
                  selectedId === s.id
                    ? 'border-cm-accent-purple text-cm-accent-purple font-medium'
                    : 'border-transparent text-cm-text-muted hover:text-cm-text-secondary hover:border-cm-border-mid'
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>

          {selectedId && (
            <div className="mt-4">
              <StakeholderDashboard key={selectedId} stakeholderId={selectedId} />
            </div>
          )}
        </>
      )}

      <NativeStakeholderForm
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSubmit={applyCommand}
        onCreated={(newId) => joinStakeholder(newId)}
      />

      {/* Join existing stakeholder picker */}
      {showJoinPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-cm-bg-deep/70" onClick={() => setShowJoinPicker(false)} />
          <div className="relative bg-cm-bg-surface border border-cm-border-mid rounded-xl shadow-2xl max-w-sm w-full mx-4 p-6">
            <h3 className="font-mono font-semibold text-cm-text-primary mb-4">Join a stakeholder group</h3>
            <div className="relative mb-3">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cm-text-muted" />
              <input
                type="text"
                value={joinQuery}
                onChange={(e) => setJoinQuery(e.target.value)}
                placeholder="Search groups..."
                autoFocus
                className="w-full pl-10 pr-4 py-2 bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:border-cm-accent-purple focus:ring-cm-accent-purple"
              />
            </div>
            {filteredJoinable.length > 0 ? (
              <div className="max-h-48 overflow-y-auto divide-y divide-cm-border-subtle border border-cm-border-subtle rounded-lg">
                {filteredJoinable.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => joinStakeholder(s.id)}
                    className="w-full px-4 py-3 text-sm text-cm-text-primary hover:bg-cm-bg-hover transition-colors text-left"
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            ) : joinableStakeholders.length === 0 ? (
              <p className="text-sm text-cm-text-muted text-center py-4">All stakeholder groups are already joined.</p>
            ) : (
              <p className="text-sm text-cm-text-muted text-center py-4">No groups match "{joinQuery}"</p>
            )}
            <button
              onClick={() => setShowJoinPicker(false)}
              className="mt-4 w-full text-center text-sm text-cm-text-muted hover:text-cm-text-primary transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default YourInterestsPage;
